import type { BusinessGoalV1, ExecutionModeV1 } from "@adpilot/shared";
import {
  AiPolicyFieldsV1Schema,
  resolveExecutionModePolicyUpdateV1,
  resolveExecutionModeV1,
} from "@adpilot/shared";
import { IconX } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBackdrop,
  DialogClose,
  DialogDescription,
  DialogPortal,
  DialogPrimitive,
  DialogTitle,
  DialogViewport,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { getApiErrorMessage } from "@/lib/api-error";
import { useFormatters } from "@/lib/format";
import { fetchSystemAiPolicy, updateSystemAiPolicy } from "@/lib/settings-api";
import { formatBeijingDateTime } from "@/lib/time";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import { AiPolicyDialogFooter } from "./ai-policy-dialog-footer";
import { type AiPolicyDialogPurpose, GOAL_OPTIONS, type SetupStep } from "./ai-policy-options";
import { PolicyStep } from "./policy-step";
import { ReviewStep } from "./review-step";
import { SetupConfetti } from "./setup-confetti";
import { SetupProgress, SetupStepPanel } from "./setup-step-panel";
import { SuccessStep } from "./success-step";

export function WorkspaceAiPolicyDialog({
  open,
  onOpenChange,
  shopName,
  eligibleCampaigns = 0,
  excludedCampaigns = 0,
  nextRunAt = null,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopName?: string;
  eligibleCampaigns?: number;
  excludedCampaigns?: number;
  nextRunAt?: string | null;
}) {
  const { t } = useTranslation("dashboard");
  const displayShopName = shopName ?? t("shopFallback");
  const queryClient = useQueryClient();
  const authUser = useAuthStore((state) => state.user);
  const authShops = useAuthStore((state) => state.shops);
  const popupRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState<SetupStep>("policy");
  const [dialogPurpose, setDialogPurpose] = useState<AiPolicyDialogPurpose>();
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false);
  const [goal, setGoal] = useState<BusinessGoalV1>("improve_efficiency");
  const [targetAcos, setTargetAcos] = useState("24");
  const [mode, setMode] = useState<ExecutionModeV1>("semi-auto");
  const [modeDirty, setModeDirty] = useState(false);
  const policyQuery = useQuery({
    queryKey: ["settings", "ai-policy"],
    queryFn: fetchSystemAiPolicy,
    enabled: open,
  });
  const isEditing = dialogPurpose === "edit";
  const workspaceShopCount = authUser?.activeWorkspaceId
    ? authShops.filter((shop) => shop.workspaceId === authUser.activeWorkspaceId).length
    : 0;

  useEffect(() => {
    if (!open || !policyQuery.data) return;
    setDialogPurpose((current) => current ?? (policyQuery.data.systemEnabled ? "edit" : "setup"));
    if (!policyQuery.data.systemEnabled) {
      setGoal("improve_efficiency");
      setTargetAcos("24");
      setMode("semi-auto");
      setModeDirty(false);
      return;
    }
    setGoal(policyQuery.data.policy.businessGoal);
    setTargetAcos(String(Math.round(policyQuery.data.policy.targetAcos * 100)));
    setMode(resolveExecutionModeV1(policyQuery.data.policy));
    setModeDirty(false);
  }, [open, policyQuery.data]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!policyQuery.data) throw new Error(t("policyDialog.errors.unavailable"));
      const modeFields = resolveExecutionModePolicyUpdateV1(
        policyQuery.data.policy,
        mode,
        modeDirty || !isEditing,
      );
      const policy = AiPolicyFieldsV1Schema.parse({
        ...policyQuery.data.policy,
        businessGoal: goal,
        targetAcos: Number(targetAcos) / 100,
        ...modeFields,
      });
      return updateSystemAiPolicy({ version: 1, systemEnabled: true, policy });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["settings", "ai-policy"], data);
      queryClient.invalidateQueries({ queryKey: ["dashboard", "summary"] });
      if (isEditing) {
        toast.success(t("policyDialog.toasts.updated"));
        resetAndClose();
      } else {
        setStep("success");
      }
    },
    onError: async (error) => toast.error(await getApiErrorMessage(error)),
  });

  const disableMutation = useMutation({
    mutationFn: () => updateSystemAiPolicy({ version: 1, systemEnabled: false }),
    onSuccess: (data) => {
      queryClient.setQueryData(["settings", "ai-policy"], data);
      queryClient.invalidateQueries({ queryKey: ["dashboard", "summary"] });
      setDisableConfirmOpen(false);
      resetAndClose();
      toast.success(t("policyDialog.toasts.disabled"));
    },
    onError: async (error) => toast.error(await getApiErrorMessage(error)),
  });

  const validAcos =
    Number.isFinite(Number(targetAcos)) && Number(targetAcos) >= 1 && Number(targetAcos) <= 200;
  const resetAndClose = () => {
    onOpenChange(false);
  };
  const targetNumber = Number(targetAcos);
  const recommendedRange = `${Math.max(1, targetNumber - 1)}–${Math.min(200, targetNumber + 1)}%`;
  const goalOption = GOAL_OPTIONS.find((item) => item.value === goal);
  const goalLabel = goalOption ? t(goalOption.labelKey) : goal;
  const modeLabel =
    mode === "recommendation"
      ? t("policyDialog.mode.recommendation")
      : mode === "semi-auto"
        ? t("policyDialog.mode.semiAuto")
        : t("policyDialog.mode.fullAuto");
  const selectMode = (nextMode: ExecutionModeV1) => {
    if (nextMode !== mode) setModeDirty(true);
    setMode(nextMode);
  };
  const { locale } = useFormatters();
  const nextRunTime = nextRunAt ? formatBeijingDateTime(nextRunAt, locale) : "—";

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      onOpenChangeComplete={(nextOpen) => {
        if (!nextOpen) {
          setStep("policy");
          setDialogPurpose(undefined);
          setDisableConfirmOpen(false);
        }
      }}
    >
      <DialogPortal>
        <DialogBackdrop className="bg-ink/35 backdrop-blur-none" />
        <DialogViewport className="grid-rows-[1fr_auto_1fr] p-4">
          {dialogPurpose === "setup" && step === "success" ? (
            <SetupConfetti anchorRef={popupRef} />
          ) : null}
          <DialogPrimitive.Popup
            ref={popupRef}
            className={cn(
              "relative z-[2] row-start-2 flex max-h-[calc(100vh-32px)] w-[600px] max-w-[calc(100vw-32px)] origin-center flex-col overflow-hidden rounded-[14px] bg-surface text-ink shadow-[var(--paper-overlay-shadow)] outline-none transition-[height,scale,opacity] duration-200 data-ending-style:scale-[0.97] data-ending-style:opacity-0 data-starting-style:scale-[0.97] data-starting-style:opacity-0",
              isEditing ? "h-[654px]" : step === "policy" ? "h-[696px]" : "h-[630px]",
            )}
          >
            <DialogClose
              aria-label={t("policyDialog.close")}
              className="absolute right-[22px] top-5 z-10"
              render={
                <Button
                  size="icon-sm"
                  variant="outline"
                  className="size-[30px] rounded-[8px] [&_svg]:m-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              }
            >
              <IconX className="size-[15px]" stroke={1.8} />
            </DialogClose>
            <div className="flex h-20 shrink-0 flex-col justify-center border-b border-line px-[22px] pr-16">
              <DialogTitle className="text-[16px] font-semibold leading-[22px]">
                {step === "success"
                  ? t("policyDialog.titles.enabled")
                  : isEditing
                    ? t("policyDialog.titles.edit")
                    : t("policyDialog.titles.setup")}
              </DialogTitle>
              <DialogDescription className="mt-0.5 text-[11px] leading-4">
                {isEditing
                  ? t("policyDialog.descriptions.edit", { eligibleCampaigns })
                  : step === "policy"
                    ? t("policyDialog.descriptions.configure", { shopName: displayShopName })
                    : step === "review"
                      ? t("policyDialog.descriptions.review", { shopName: displayShopName })
                      : t("policyDialog.descriptions.success", { shopName: displayShopName })}
              </DialogDescription>
            </div>
            {isEditing ? null : <SetupProgress step={step} />}
            <div className="relative min-h-0 flex-1 overflow-hidden">
              {policyQuery.isLoading && step !== "success" ? (
                <div className="p-[22px]">
                  <Skeleton className="h-full min-h-[390px] w-full rounded-[10px]" />
                </div>
              ) : (
                <>
                  <SetupStepPanel activeStep={step} stepId="policy">
                    <PolicyStep
                      goal={goal}
                      setGoal={setGoal}
                      targetAcos={targetAcos}
                      setTargetAcos={setTargetAcos}
                      validAcos={validAcos}
                      recommendedRange={recommendedRange}
                      mode={mode}
                      selectMode={selectMode}
                    />
                  </SetupStepPanel>
                  <SetupStepPanel activeStep={step} stepId="review">
                    <ReviewStep
                      displayShopName={displayShopName}
                      goalLabel={goalLabel}
                      targetAcos={targetAcos}
                      modeLabel={modeLabel}
                      mode={mode}
                      eligibleCampaigns={eligibleCampaigns}
                      excludedCampaigns={excludedCampaigns}
                      nextRunTime={nextRunTime}
                      managedGroupResumeImpact={policyQuery.data?.managedGroupResumeImpact}
                    />
                  </SetupStepPanel>
                  <SetupStepPanel activeStep={step} stepId="success">
                    <SuccessStep
                      eligibleCampaigns={eligibleCampaigns}
                      nextRunTime={nextRunTime}
                      goalLabel={goalLabel}
                      targetAcos={targetAcos}
                      modeLabel={modeLabel}
                    />
                  </SetupStepPanel>
                </>
              )}
            </div>
            <AiPolicyDialogFooter
              isEditing={isEditing}
              step={step}
              setStep={setStep}
              eligibleCampaigns={eligibleCampaigns}
              workspaceShopCount={workspaceShopCount}
              disableConfirmOpen={disableConfirmOpen}
              setDisableConfirmOpen={setDisableConfirmOpen}
              disablePending={disableMutation.isPending}
              onDisable={() => disableMutation.mutate()}
              validAcos={validAcos}
              policyLoading={policyQuery.isLoading}
              savePending={mutation.isPending}
              onSave={() => mutation.mutate()}
              resetAndClose={resetAndClose}
            />
          </DialogPrimitive.Popup>
        </DialogViewport>
      </DialogPortal>
    </Dialog>
  );
}
